import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import './NewWorldDbSearch.css';

/**
 * NewWorldDbSearch - Stabile Suchkomponente nach Best Practices
 * 
 * WICHTIG: Diese Komponente ist jetzt stabil bei schnellem Tippen.
 * Der Focus wird nicht mehr durch Re-Renders verloren.
 */
const NewWorldDbSearch = ({ 
  onSearch, 
  value = '', 
  isSearching = false,
  placeholder = 'Search contacts...',
  needsMigration = false,
  onMigrate = null,
  isMigrating = false,
  migrationProgress = 0
}) => {
  // Lokaler State nur für UI (expanded/focused)
  const [isExpanded, setIsExpanded] = useState(!!value);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  
  // Ref um zu tracken ob wir gerade tippen (verhindert ungewolltes Schließen)
  const isTypingRef = useRef(false);
  const typingTimeoutRef = useRef(null);

  // Sync expanded state mit value - aber nur wenn nicht leer
  useEffect(() => {
    if (value) {
      setIsExpanded(true);
    }
  }, [value]);

  // Keyboard-Handler
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      if (value) {
        onSearch?.('');
      } else {
        setIsExpanded(false);
        inputRef.current?.blur();
      }
    }
  }, [value, onSearch]);

  // Icon-Click expandiert die Suche
  const handleIconClick = useCallback(() => {
    setIsExpanded(true);
    // Focus sofort setzen
    setTimeout(() => inputRef.current?.focus(), 0);
  }, []);

  // Input-Änderungen - markiere als "typing"
  const handleChange = useCallback((e) => {
    const newValue = e.target.value;
    
    // Markiere als typing um Blur zu verhindern
    isTypingRef.current = true;
    
    // Clear vorheriges timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Nach 500ms typing-flag resetten
    typingTimeoutRef.current = setTimeout(() => {
      isTypingRef.current = false;
    }, 500);
    
    onSearch?.(newValue);
  }, [onSearch]);

  // Clear-Button
  const handleClear = useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    onSearch?.('');
    // Focus behalten
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [onSearch]);

  // Focus-Handler
  const handleFocus = useCallback(() => {
    setIsExpanded(true);
  }, []);

  // Blur-Handler - SEHR konservativ, schließt nur wenn wirklich gewollt
  const handleBlur = useCallback((e) => {
    // Wenn wir gerade tippen, nicht schließen
    if (isTypingRef.current) {
      return;
    }
    
    // Wenn Wert vorhanden, nicht schließen
    if (value) {
      return;
    }
    
    // Wenn der neue Focus-Target innerhalb des Containers ist, nicht schließen
    const relatedTarget = e.relatedTarget;
    if (relatedTarget && containerRef.current?.contains(relatedTarget)) {
      return;
    }
    
    // Verzögert schließen, nur wenn wirklich außerhalb geklickt wurde
    setTimeout(() => {
      // Nochmal prüfen ob wir nicht wieder fokussiert sind
      if (!containerRef.current?.contains(document.activeElement) && !value && !isTypingRef.current) {
        setIsExpanded(false);
      }
    }, 200);
  }, [value]);

  // Cleanup timeouts
  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  // Memoized class string für Performance
  const containerClasses = useMemo(() => {
    return [
      'new-world-db-search-container',
      isExpanded ? 'expanded' : '',
      isSearching ? 'searching' : '',
      value ? 'has-value' : ''
    ].filter(Boolean).join(' ');
  }, [isExpanded, isSearching, value]);

  return (
    <div className="new-world-db-search-wrapper">
      <div 
        ref={containerRef}
        className={containerClasses}
        role="search"
        aria-label="Search contacts"
      >
        {/* Such-Icon */}
        <button
          type="button"
          className="new-world-db-search-icon"
          onClick={handleIconClick}
          aria-label="Open search"
          tabIndex={isExpanded ? -1 : 0}
        >
          {isSearching ? (
            <svg className="search-spinner" width="16" height="16" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" 
                strokeLinecap="round" strokeDasharray="31.416" strokeDashoffset="10" />
            </svg>
          ) : (
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
              <path d="M10 18a8 8 0 1 1 8-8 8.009 8.009 0 0 1-8 8zm0-14a6 6 0 1 0 0 12 6 6 0 0 0 0-12z" fill="currentColor"/>
              <path d="M21.707 20.293l-4-4a1 1 0 0 0-1.414 1.414l4 4a1 1 0 0 0 1.414-1.414z" fill="currentColor"/>
            </svg>
          )}
        </button>

        {/* Input */}
        <input
          ref={inputRef}
          type="text"
          className="new-world-db-search-input"
          placeholder={placeholder}
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          aria-label="Search input"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck="false"
        />

        {/* Clear Button */}
        {value && (
          <button
            type="button"
            className="new-world-db-search-clear"
            onClick={handleClear}
            onMouseDown={(e) => e.preventDefault()}
            aria-label="Clear search"
            tabIndex={-1}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" 
                strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
      </div>

      {/* Migration Button - nur anzeigen wenn nötig */}
      {needsMigration && onMigrate && (
        <button
          type="button"
          className={`new-world-db-search-migrate ${isMigrating ? 'migrating' : ''}`}
          onClick={onMigrate}
          disabled={isMigrating}
          title="Enable fast search for all contacts"
        >
          {isMigrating ? (
            <>
              <svg className="migrate-spinner" width="14" height="14" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" 
                  strokeLinecap="round" strokeDasharray="31.416" strokeDashoffset="10" />
              </svg>
              <span>{migrationProgress}%</span>
            </>
          ) : (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke="currentColor" strokeWidth="2" 
                  strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>Enable Search</span>
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default NewWorldDbSearch;
